import { useCallback, useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { useAtom } from 'jotai';

import {
  canMoveNodes,
  type FileSystemSnapshot,
  type MoveVerdict,
} from '../helpers/fileSystemMove.js';
import { isFolderNode, toFileNode } from '../helpers/fileSystemNode.js';
import { getDocumentsOption } from '../queries/rag.js';
import { draftFoldersAtom, fileLocationsAtom } from '../store/fileSystem.js';
import type { FileSystemNode } from '../types/fileSystem.js';

/**
 * Containment is client state for now, so this writes the two atoms directly.
 * Swapping in a mutation later should leave this surface untouched.
 */
export const useMoveNodes = () => {
  const [folders, setFolders] = useAtom(draftFoldersAtom);
  const [fileLocations, setFileLocations] = useAtom(fileLocationsAtom);
  const { data: documents = [] } = useQuery(getDocumentsOption());

  const snapshot = useMemo<FileSystemSnapshot>(
    () => ({ folders, files: documents.map(toFileNode), fileLocations }),
    [folders, documents, fileLocations],
  );

  const checkMove = useCallback(
    (nodes: FileSystemNode[], targetFolderId: string) =>
      canMoveNodes(nodes, targetFolderId, snapshot),
    [snapshot],
  );

  const moveNodes = useCallback(
    (nodes: FileSystemNode[], targetFolderId: string): MoveVerdict => {
      const verdict = checkMove(nodes, targetFolderId);

      if (!verdict.allowed) return verdict;

      const movedFolderIds = new Set(
        nodes.filter(isFolderNode).map((folder) => folder.id),
      );
      const movedFileIds = nodes
        .filter((node) => !isFolderNode(node))
        .map((file) => file.id);

      // Descendants follow their parent for free — they only ever reference
      // parentId, so re-parenting the dragged folder re-parents its subtree.
      if (movedFolderIds.size) {
        setFolders((prev) =>
          prev.map((folder) =>
            movedFolderIds.has(folder.id)
              ? { ...folder, parentId: targetFolderId }
              : folder,
          ),
        );
      }

      if (movedFileIds.length) {
        setFileLocations((prev) => ({
          ...prev,
          ...Object.fromEntries(movedFileIds.map((id) => [id, targetFolderId])),
        }));
      }

      return verdict;
    },
    [checkMove, setFolders, setFileLocations],
  );

  return { checkMove, moveNodes };
};
